import type { AttentionSeverity } from '@@/api/attention/attention-items';
import type { Notification } from 'types';
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useLocalStorageState } from 'hooks/useLocalStorageState';
import { cn } from 'helpers/cn';
import { useAttention } from '@/state/useAttention';
import { useWebsites } from '@/state/useWebsites';
import { useNotifications, useUnreadCount } from '@/state/notifications/useNotifications';
import { describe } from './Notifications/descriptors';
import { describeAttention } from '../Home/attention-descriptors';

type LogTab = 'activity' | 'attention';

const SEVERITY_COLOUR: Record<AttentionSeverity, string> = {
  broken: 'text-danger',
  decide: 'text-warn',
  queue: 'text-dim',
};

/**
 * The console's bottom pane. Two feeds that answer different questions, so that
 * neither is a second copy of something already on the page:
 *
 * - activity  — what has happened, in order, from the notification feed. This is
 *               the only genuine event stream in the product; it was previously
 *               reachable only by opening the bell.
 * - attention — what is wrong right now, from the same poll the dashboard's
 *               attention card reads.
 *
 * The design sheet labels these "runtime" and "tracking". Nothing in the product
 * emits runtime or tracking events, so the tabs are named after what they
 * actually show rather than after a feed that does not exist.
 *
 * A proportional strip, never a fixed track, so it gives room back to the page
 * body on a short viewport.
 */
export const LogPanel = () => {
  const [tab, setTab] = useState<LogTab>('activity');
  const [isCollapsed, setIsCollapsed] = useLocalStorageState('pertento-log-collapsed', false);
  const { website } = useWebsites();
  const { items } = useAttention(website?.id);
  const unread = useUnreadCount();

  const broken = items?.filter((item) => item.severity === 'broken').length ?? 0;

  return (
    <section
      className={cn(
        'bg-panel border-line flex shrink-0 flex-col overflow-hidden rounded-panel border',
        isCollapsed ? 'basis-auto' : 'max-h-[240px] min-h-[150px] basis-[28%]',
      )}
    >
      <div className="border-line flex shrink-0 items-center gap-1 border-b px-3">
        <LogTabButton tab="activity" active={tab} onSelect={setTab} dotClass="bg-accent" count={unread} />
        <LogTabButton tab="attention" active={tab} onSelect={setTab} dotClass={broken ? 'bg-danger' : 'bg-ok'} />

        {/* Collapsed, the strip still has to say whether anything is wrong -
            otherwise minimising it means losing the signal, not just the space. */}
        {isCollapsed && broken > 0 && <span className="text-danger ml-2 font-mono text-micro">{broken} broken</span>}

        <button
          type="button"
          onClick={() => setIsCollapsed((previous) => !previous)}
          aria-label={isCollapsed ? 'Expand log panel' : 'Collapse log panel'}
          aria-expanded={!isCollapsed}
          className="text-dim hover:text-foreground hover:bg-hover ml-auto flex size-6 items-center justify-center rounded transition-colors duration-150"
        >
          <ChevronDown className={cn('size-4 transition-transform duration-150', isCollapsed && 'rotate-180')} />
        </button>
      </div>

      {!isCollapsed && (
        <div className="min-h-0 flex-1 overflow-hidden">
          {tab === 'activity' ? <ActivityPane isOpen /> : <AttentionPane />}
        </div>
      )}
    </section>
  );
};

type LogTabButtonProps = {
  tab: LogTab;
  active: LogTab;
  onSelect: (tab: LogTab) => void;
  dotClass: string;
  count?: number;
};

const LogTabButton = ({ tab, active, onSelect, dotClass, count }: LogTabButtonProps) => (
  <button
    type="button"
    onClick={() => onSelect(tab)}
    className={cn(
      '-mb-px flex items-center gap-1.5 border-b px-2 py-2.5 font-mono text-micro transition-colors duration-150',
      tab === active ? 'border-ok text-foreground' : 'text-dim hover:text-foreground border-transparent',
    )}
  >
    <span className={cn('size-1.5 rounded-pill', dotClass)} />
    {tab}
    {!!count && <span className="text-accent">{count > 9 ? '9+' : count}</span>}
  </button>
);

type PaneHeadProps = { source: string; state: string };

const PaneHead = ({ source, state }: PaneHeadProps) => (
  <div className="border-line flex shrink-0 items-center justify-between gap-3 border-b px-3 py-1.5">
    <span className="log-line text-dim truncate">{source}</span>
    <span className="log-line text-dim shrink-0">{state}</span>
  </div>
);

const relativeTime = (timestamp: number): string => {
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 60) return 'now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
};

type ActivityPaneProps = { isOpen: boolean };

/**
 * The notification feed as log lines. Only fetched while this tab is showing —
 * the unread badge is what polls, and it costs one indexed count rather than a
 * page of joined rows.
 */
const ActivityPane = ({ isOpen }: ActivityPaneProps) => {
  const navigate = useNavigate();
  const { notifications, isLoading } = useNotifications(isOpen);

  return (
    <div className="flex h-full flex-col">
      <PaneHead source="activity · all experiments" state={isLoading ? 'loading' : 'polling 60s'} />
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
        {!isLoading && !notifications.length && <p className="supporting">Nothing has happened yet.</p>}
        {notifications.map((notification: Notification) => (
          <ActivityLine key={notification.id} notification={notification} onNavigate={navigate} />
        ))}
      </div>
    </div>
  );
};

type ActivityLineProps = { notification: Notification; onNavigate: (to: string) => void };

const ActivityLine = ({ notification, onNavigate }: ActivityLineProps) => {
  const descriptor = describe(notification);
  const target = descriptor.link(notification);
  const title = descriptor.title(notification.payload);

  return (
    <button
      type="button"
      disabled={!target}
      onClick={() => target && onNavigate(target)}
      className={cn(
        'log-line hover:bg-hover flex w-full items-baseline gap-2 rounded px-1 py-px text-left',
        !target && 'cursor-default',
      )}
    >
      <span className="text-dim shrink-0">[{notification.type}]</span>
      <span className={cn('min-w-0 flex-1 truncate', descriptor.tone)}>{title}</span>
      <span className="text-dim shrink-0">{relativeTime(new Date(notification.createdAt).getTime())}</span>
    </button>
  );
};

/** What is wrong right now, from the same poll the dashboard's attention card reads. */
const AttentionPane = () => {
  const { website } = useWebsites();
  const { items, isLoading } = useAttention(website?.id);
  const source = website?.friendlyName || website?.url || 'all websites';

  return (
    <div className="flex h-full flex-col">
      <PaneHead source={`attention · ${source}`} state={isLoading ? 'loading' : 'polling 60s'} />
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
        {!isLoading && !items?.length && <p className="supporting">Nothing needs attention on this website.</p>}
        {items?.map((item) => {
          const { title } = describeAttention(item);
          return (
            <div key={`${item.kind}-${item.experimentId}`} className="log-line flex items-baseline gap-2 px-1 py-px">
              <span className="text-dim shrink-0">[{item.kind}]</span>
              <span className={cn('min-w-0 flex-1 truncate', SEVERITY_COLOUR[item.severity])}>{title(item)}</span>
              <span className="text-dim shrink-0">{item.experimentId}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
